import { createApi } from "@reduxjs/toolkit/dist/query/react"
import {
  ACTIVITY_ENDPOINT,
  ACTIVITY_FEED_NEW_ENDPOINT
} from "../../common/constants"
import { getQueryString } from "../../common/utils"
import { Activity, APIError } from "../../types"
import { baseQuery } from "../api"

const reducerPath = "activities"

export const activitiesApi = createApi({
  reducerPath,
  baseQuery,
  tagTypes: [reducerPath],
  endpoints: builder => ({
    fetchActivitiesByBrand: builder.query({
      query: (params: { brand_id: string; limit?: number; days?: number }) =>
        `${ACTIVITY_ENDPOINT}${getQueryString({ ...params, type: "brand" })}`,
      transformResponse: (responseData: Activity[] | APIError) =>
        "status" in responseData ? [] : responseData || []
    }),
    fetchActivitiesByLo: builder.query({
      query: (params: { loid: string; limit?: number; days?: number }) =>
        `${ACTIVITY_ENDPOINT}${getQueryString({ ...params, type: "lo" })}`,
      transformResponse: (responseData: Activity[] | APIError) =>
        "status" in responseData ? [] : responseData
    }),
    fetchActivitiesByOrg: builder.query({
      query: (params: { org_id: string; limit?: number; days?: number }) =>
        `${ACTIVITY_ENDPOINT}${getQueryString({ ...params, type: "org" })}`,
      transformResponse: (responseData: Activity[] | APIError) =>
        "status" in responseData ? [] : responseData
    }),
    fetchActivityFeedNew: builder.query({
      query: (params: {
        loid?: number
        brand_id?: number
        org_id?: number
        /** One contact's history across lead + subscription rows */
        email?: string
        /** Lookback in days when timestamp_min is not given (server default 90) */
        range?: number
        limit?: number
        days?: number
        offset?: number
        before_timestamp?: number
        timestamp_min?: number
        timestamp_max?: number
        metrics?: string
      }) => `${ACTIVITY_FEED_NEW_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (
        responseData:
          | { status: string; data: Activity[] }
          | Activity[]
          | APIError
      ) => {
        // Handle the new API format: { status: "success", data: [...] }
        if (
          responseData &&
          typeof responseData === "object" &&
          "status" in responseData &&
          "data" in responseData
        ) {
          return responseData.data || []
        }
        // Handle error responses
        if (
          responseData &&
          typeof responseData === "object" &&
          "status" in responseData &&
          responseData.status === "error"
        ) {
          return []
        }
        // Handle direct array response (legacy format)
        return Array.isArray(responseData) ? responseData : []
      },
      serializeQueryArgs: ({ queryArgs }) => {
        // Serialize base query args (without pagination params) for cache key
        const { offset, limit, ...baseArgs } = queryArgs
        return baseArgs
      },
      merge: (currentCache, newItems, { arg }) => {
        // Merge new items with existing cache for infinite scroll
        // If offset is 0 or undefined, it's the first page - replace cache
        if (!arg.offset || arg.offset === 0) {
          return newItems
        }
        // Otherwise, append new items to existing cache
        return [...currentCache, ...newItems]
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        // Force refetch when pagination params change
        return currentArg?.offset !== previousArg?.offset
      }
    })
  })
})

export const {
  useFetchActivitiesByBrandQuery,
  useFetchActivitiesByLoQuery,
  useFetchActivitiesByOrgQuery,
  useFetchActivityFeedNewQuery
} = activitiesApi
